import React, {useState, useEffect} from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Alert from "@mui/material/Alert";
import CircularProgress from "@mui/material/CircularProgress";
import PriceChangeOutlinedIcon from "@mui/icons-material/PriceChangeOutlined";
import Box from "@mui/material/Box";

export const BookPriceEditDialog = ({open, onClose, currentPrice, onSavePrice}) => {
    const [priceValue, setPriceValue] = useState(String(currentPrice || 599));
    const [error, setError] = useState(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (open) {
            setPriceValue(String(currentPrice || 599));
            setError(null);
            setSaving(false);
        }
    }, [open, currentPrice]);

    const handleSave = async () => {
        const num = Number(priceValue);
        if (isNaN(num) || num <= 0) {
            setError("Prisen må være et positivt tall.");
            return;
        }
        if (num > 10000) {
            setError("Prisen kan ikke overstige 10 000 kr.");
            return;
        }

        setError(null);
        setSaving(true);
        try {
            await onSavePrice(num);
            onClose();
        } catch (err) {
            setError(err.message || "Kunne ikke lagre prisen.");
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog
            open={Boolean(open)}
            onClose={saving ? undefined : onClose}
            maxWidth="xs"
            fullWidth
            PaperProps={{
                sx: {borderRadius: 3, p: 1},
            }}
            aria-labelledby="edit-price-dialog-title"
        >
            <DialogTitle id="edit-price-dialog-title" sx={{display: "flex", alignItems: "center", gap: 1.5, pb: 1}}>
                <PriceChangeOutlinedIcon color="primary" />
                <Typography variant="h6" component="span" sx={{fontWeight: 700}}>
                    Endre pris på arbeidsbøker
                </Typography>
            </DialogTitle>

            <DialogContent dividers sx={{borderTop: "none", pt: 1}}>
                <Typography variant="body2" color="text.secondary" sx={{mb: 2.5}}>
                    Oppgi ny fast pris per eksemplar. Prisen oppdateres umiddelbart for alle som besøker bestillingssiden.
                </Typography>

                {error && (
                    <Alert severity="error" sx={{mb: 2, borderRadius: 2}}>
                        {error}
                    </Alert>
                )}

                <TextField
                    autoFocus
                    fullWidth
                    id="book-price-input"
                    label="Ny pris (kr inkl. mva og frakt)"
                    type="number"
                    value={priceValue}
                    onChange={(e) => setPriceValue(e.target.value)}
                    disabled={saving}
                    inputProps={{min: 1, max: 10000}}
                    helperText="Standardpris er 599 kr"
                />
            </DialogContent>

            <DialogActions sx={{px: 3, py: 2}}>
                <Button onClick={onClose} disabled={saving} sx={{textTransform: "none", color: "text.secondary"}}>
                    Avbryt
                </Button>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={handleSave}
                    disabled={saving}
                    startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
                    sx={{textTransform: "none", fontWeight: 700, borderRadius: 2, px: 2.5}}
                >
                    {saving ? "Lagrer..." : "Lagre ny pris"}
                </Button>
            </DialogActions>
        </Dialog>
    );
};
